import { useEffect, useMemo } from 'react';
import { Box, Paragraph } from '@zillow/constellation-10';
import hasMissingAttachmentContent from '../../../utils/hasMissingAttachmentContent';
import ExpiredAttachmentsText from '../ExpiredAttachmentsText';
import ImageThumbnail from '../ImageThumbnail';
import type { Attachment } from '../../../types/attachment';

type Props = {
  imageAttachments: Array<Attachment>;
  isMessageOwner: boolean;
  onClick(zDocId: string): void;
  overflowCount: number;
  onLogError: () => void;
  onExpiredDetailsClick: () => void;
  impersonationDetectedValue?: string;
  impersonationTooltipText?: string;
};

const MAX_VISIBLE_IMAGES = 6;
const SINGLE_IMAGE_SIZE = 160;
const MULTI_IMAGE_SIZE = 94;

export default function MessageImageGrid({
  imageAttachments,
  isMessageOwner,
  onClick,
  overflowCount,
  onLogError,
  onExpiredDetailsClick,
  impersonationDetectedValue = 'impersonation-detected',
  impersonationTooltipText = 'Cannot view while impersonating',
}: Props): JSX.Element {
  const showOverflowCount = overflowCount > 0;
  const isSingleImage = imageAttachments.length === 1;

  // Calculate derived states from attachments
  const { showExpiredText, hasMissingContentError } = useMemo(() => {
    let hasExpired = false;
    let hasMissing = false;

    imageAttachments.forEach((attachment) => {
      if (attachment.isAttachmentExpired) {
        hasExpired = true;
      }
      if (hasMissingAttachmentContent(attachment.attachmentMetadataStatus, attachment.isAttachmentExpired)) {
        hasMissing = true;
      }
    });

    return {
      showExpiredText: hasExpired,
      hasMissingContentError: hasMissing,
    };
  }, [imageAttachments]);

  useEffect(() => {
    if (hasMissingContentError) {
      onLogError();
    }
  }, [hasMissingContentError, onLogError]);

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: isMessageOwner ? 'flex-end' : 'flex-start' }}>
      <Box
        css={{
          display: 'inline-flex',
          flexWrap: 'wrap',
          gap: 'tightest',
          maxWidth: '192px',
          position: 'relative',
          marginBottom: 'tighter',
          justifyContent: isMessageOwner ? 'flex-end' : 'flex-start',
          md: {
            maxWidth: '290px',
          },
        }}
      >
        {imageAttachments.map((imageAttachment, index) => {
          const { attachmentMetadataStatus, fileName, isAttachmentExpired, previewUrl, zDocId } = imageAttachment;

          const hasMissingContent = hasMissingAttachmentContent(attachmentMetadataStatus, isAttachmentExpired);
          const shouldDarkenImage = showOverflowCount && index === MAX_VISIBLE_IMAGES - 1;
          const imageSize = isSingleImage ? SINGLE_IMAGE_SIZE : MULTI_IMAGE_SIZE;
          const isImpersonator = previewUrl === impersonationDetectedValue;
          const shouldShowPlaceholder = !previewUrl || isAttachmentExpired || isImpersonator || hasMissingContent;
          const key = `${fileName}-${zDocId}`;

          const handleImageThumbnailClick = () => {
            if (zDocId) onClick(zDocId);
          };

          return (
            <ImageThumbnail
              key={key}
              fileName={fileName || ''}
              onClick={handleImageThumbnailClick}
              isImpersonator={isImpersonator}
              shouldDarkenImage={shouldDarkenImage}
              shouldShowExpiredCaption={isSingleImage && !!isAttachmentExpired}
              shouldShowPlaceholder={shouldShowPlaceholder}
              size={imageSize}
              url={previewUrl || ''}
              impersonationTooltipText={impersonationTooltipText}
            />
          );
        })}

        {showOverflowCount && !showExpiredText && (
          <Paragraph
            data-testid="image-overflow-count"
            textStyle="body-lg-bold"
            css={{ color: 'white', position: 'absolute', bottom: 'tighter', right: 'tighter' }}
          >
            {`+${overflowCount}`}
          </Paragraph>
        )}
      </Box>

      {showExpiredText && (
        <ExpiredAttachmentsText isPlural={!isSingleImage} onExpiredDetailsClick={onExpiredDetailsClick} />
      )}
    </Box>
  );
}
